import { EAiContextLevel } from '../../shared/components/ai-assist-shell/ai-assist-shell.interface';

/** Object types a whiteboard can belong to, as the URL and the API name them (`/whiteboard/:objectType/:objectId`). */
export enum EWhiteboardObjectType {
  ishikawaCause = 'ishikawa-cause',
  item = 'item',
}

/** A shape keeps its geometry, so it can be selected, moved and resized; `path` is a freehand trail. */
export enum EWhiteboardNodeType {
  path = 'path',
  rect = 'rect',
  ellipse = 'ellipse',
  line = 'line',
  arrow = 'arrow',
  text = 'text',
}

/** The tools of the tool box; each one draws exactly one node (`select` and `eraser` draw nothing). */
export enum EWhiteboardEditorTool {
  select = 'select',
  pen = 'pen',
  eraser = 'eraser',
  line = 'line',
  arrow = 'arrow',
  rectangle = 'rectangle',
  text = 'text',
}

export enum EWhiteboardStrokeSize {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

export enum EWhiteboardError {
  notFound = 'notFound',
  loadFailed = 'loadFailed',
}

/** `size` is the line width, or the font size for text. */
interface IWhiteboardNodeBase {
  id: string;
  color: string;
  size: number;
}

/** One continuous pen drag; `points` is flat `[x, y, pressure, x, y, pressure, …]` in board coordinates. */
export interface IWhiteboardPathNode extends IWhiteboardNodeBase {
  type: EWhiteboardNodeType.path;
  points: number[];
}

export interface IWhiteboardRectNode extends IWhiteboardNodeBase {
  type: EWhiteboardNodeType.rect;
  /** Top-left corner; `width` and `height` are never negative (a drag is normalised when it ends). */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface IWhiteboardEllipseNode extends IWhiteboardNodeBase {
  type: EWhiteboardNodeType.ellipse;
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

/** A straight line, or an arrow whose head sits at (`x2`, `y2`). */
export interface IWhiteboardSegmentNode extends IWhiteboardNodeBase {
  type: EWhiteboardNodeType.line | EWhiteboardNodeType.arrow;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** A label; (`x`, `y`) is the top-left of its first line, `size` is the font size, lines are separated by "\n". */
export interface IWhiteboardTextNode extends IWhiteboardNodeBase {
  type: EWhiteboardNodeType.text;
  x: number;
  y: number;
  text: string;
}

export type TWhiteboardNode =
  | IWhiteboardPathNode
  | IWhiteboardRectNode
  | IWhiteboardEllipseNode
  | IWhiteboardSegmentNode
  | IWhiteboardTextNode;

/** The drawing as the API stores it; `version` is the format version, not the save revision. */
export interface IWhiteboardContent {
  version: number;
  width: number;
  height: number;
  nodes: TWhiteboardNode[];
}

export interface IWhiteboard {
  id: number;
  content: IWhiteboardContent;
  /** Optimistic lock: a save names the revision it was drawn on. */
  revision: number;
  changedAt: string | null;
  createdAt: string;
}

export interface IWhiteboardContext {
  /** The board has an AI agent, so AI assist can be offered. */
  hasBoardAiAgent: boolean;
  /** The item the object belongs to; the header links its key to the item modal. */
  itemId: number;
  itemKey: string;
  itemName: string;
  title: string;
}

export interface IWhiteboardView {
  context: IWhiteboardContext;
  whiteboard: IWhiteboard | null;
}

/**
 * An item whiteboard is addressed by the item's key ("EXE-25"), which is what the item modal links to; every other
 * object type, and older item links, carry the numeric id.
 */
export interface IWhiteboardRouteParams {
  objectType: EWhiteboardObjectType;
  objectRef: string;
}

/** What the whiteboard dialog closes with; undefined when it was closed by a navigation. */
export interface IWhiteboardDialogResult {
  hasWhiteboard: boolean;
}

/** What the API is called with, once the URL's reference is resolved to the object's id. */
export interface IWhiteboardRoute {
  objectType: EWhiteboardObjectType;
  objectId: number;
}

/** Turns an item key into the id the whiteboard endpoints take. */
export interface IWhiteboardItemRef {
  id: number;
}

export interface IWhiteboardSaveRequest {
  content: IWhiteboardContent;
  revision: number | null;
}

export interface IWhiteboardObjectTypeDefinition {
  labelKey: string;
  /** False when the title already is the item name, so the header's info line would only repeat it. */
  showsTitleAsInfo: boolean;
}

export interface IWhiteboardColor {
  value: string;
  labelKey: string;
}

export interface IWhiteboardSizeOption {
  key: EWhiteboardStrokeSize;
  labelKey: string;
  /** Board units: pen and shape line width, and the font size of text. */
  strokeWidth: number;
  fontSize: number;
  /** Diameter of the preview dot in the tool box, in CSS pixels. */
  dotSize: number;
}

export interface IWhiteboardToolOption {
  tool: EWhiteboardEditorTool;
  /** Material Icons ligature, or the name of an SVG icon registered by the tool box when `isSvgIcon`. */
  icon: string;
  isSvgIcon: boolean;
  labelKey: string;
  shortcut: string;
}

export interface IWhiteboardPoint {
  x: number;
  y: number;
}

/** Bounding box in board units. */
export interface IWhiteboardRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The eight grips of the selection box; each one moves the edges its name points at. */
export enum EWhiteboardResizeHandle {
  topLeft = 'topLeft',
  top = 'top',
  topRight = 'topRight',
  right = 'right',
  bottomRight = 'bottomRight',
  bottom = 'bottom',
  bottomLeft = 'bottomLeft',
  left = 'left',
}

export interface IWhiteboardHandlePoint {
  handle: EWhiteboardResizeHandle;
  x: number;
  y: number;
}

/** A finished move: how far the node was dragged, in board units. */
export interface IWhiteboardNodeMove {
  id: string;
  dx: number;
  dy: number;
}

/** `POST whiteboards/:objectType/:objectId/ai-assist` body. */
export interface IWhiteboardAiRequest {
  action: string | null;
  prompt: string | null;
  contextLevel: EAiContextLevel;
}

/** The agent's drawing as board nodes; nothing is saved yet. */
export interface IWhiteboardAiDrawing {
  nodes: TWhiteboardNode[];
}

export interface IWhiteboardAiDialogData {
  route: IWhiteboardRoute;
  /** With nothing on the board the drawing simply goes on it; otherwise the user picks add or replace. */
  isBoardEmpty: boolean;
}

export enum EWhiteboardAiApplyMode {
  add = 'add',
  replace = 'replace',
}

/** What the dialog closes with when the user applies the drawing. */
export interface IWhiteboardAiResult {
  nodes: TWhiteboardNode[];
  mode: EWhiteboardAiApplyMode;
}
